import { calculateIdealBodyWeight } from "./ibw";
import { calculateLeanBodyWeight } from "./lbw";
import { calculateAdjustedBodyWeight, normaliseCreatinine } from "./general";
import { calculateCrCl } from "./general";
import {
  BMI_THRESHOLDS,
  CRCL_THRESHOLDS,
  AMINOGLYCOSIDE_DOSING,
} from "./constants";

export function calculateAminoglycosideInitialDose({
  antibiotic,
  sex,
  heightCm,
  actualWeightKg,
  age,
  serumCreatinine,
  serumCreatinineUnit = "micromoles/L",
  renalUnknown = false,
  critical = false,
}) {
  if (!["Amikacin", "Gentamicin", "Tobramycin"].includes(antibiotic)) {
    return { error: "Invalid antibiotic." };
  }
  if (!sex || !heightCm || !actualWeightKg || !age) {
    return { error: "Missing required input." };
  }
  if (!renalUnknown && !serumCreatinine) {
    return {
      error: "Serum creatinine required if kidney function is not unknown.",
    };
  }

  const serumCrMicromol = normaliseCreatinine(
    serumCreatinine,
    serumCreatinineUnit,
  );

  const safeActualWeightKg =
    typeof actualWeightKg === "number" &&
    !isNaN(actualWeightKg) &&
    actualWeightKg > 0
      ? actualWeightKg
      : null;

  const ibw = calculateIdealBodyWeight({ sex, height: heightCm });
  const abw = calculateAdjustedBodyWeight({
    ibw: ibw,
    actualBodyWeightKg: safeActualWeightKg,
  });
  const bmi =
    safeActualWeightKg && heightCm
      ? safeActualWeightKg / Math.pow(heightCm / 100, 2)
      : null;

  // weight for CrCl: use clinically appropriate weight based on BMI
  // BMI < 18.5: actual weight, BMI 18.5-25: IBW, BMI > 25: ABW
  let weightForCrCl;
  let weightTypeForCrCl = null; // Only set when CrCl is actually calculated
  if (bmi < BMI_THRESHOLDS.UNDERWEIGHT) {
    weightForCrCl = safeActualWeightKg;
    if (!renalUnknown && !critical) {
      weightTypeForCrCl = "actual body weight";
    }
  } else if (bmi <= BMI_THRESHOLDS.NORMAL_MAX) {
    weightForCrCl = ibw;
    if (!renalUnknown && !critical) {
      weightTypeForCrCl = "ideal body weight";
    }
  } else {
    weightForCrCl = abw;
    if (!renalUnknown && !critical) {
      weightTypeForCrCl = "adjusted body weight";
    }
  }

  const lbwRaw = calculateLeanBodyWeight({
    sex,
    heightCm,
    actualBodyWeightKg: safeActualWeightKg,
    precision: -1, // bypass rounding completely
  });

  let crcl = null;

  if (!renalUnknown && !critical) {
    crcl = calculateCrCl({
      age,
      sex,
      weightKg: weightForCrCl,
      serumCreatinineMicromol: serumCrMicromol,
    });
  }

  let dose = null;
  let dosingNote = "";
  let rawDose = null;
  let mgPerKg = null;
  let cidnoround = null; // unrounded calculated dose
  let isCapped = false; // track if dose was capped at maximum
  let maxDoseUsed = null; // track which maximum dose was applied

  if (antibiotic === "Amikacin") {
    const dosing = AMINOGLYCOSIDE_DOSING.AMIKACIN;

    // treat renalUnknown as normal renal function (use highest mg/kg/cap)
    if (renalUnknown || crcl > CRCL_THRESHOLDS.NORMAL || critical) {
      mgPerKg = dosing.NORMAL_CRITICAL;
    } else if (crcl < CRCL_THRESHOLDS.LOW) {
      mgPerKg = dosing.REDUCED;
    } else {
      mgPerKg = dosing.INTERMEDIATE;
    }

    rawDose = mgPerKg * lbwRaw; // use full precision LBW for calculation
    cidnoround = rawDose;

    // only round at the final step
    let finalDose = rawDose;
    if (critical || renalUnknown) {
      // for critical/ICU or renalUnknown, always cap at maximum dose
      if (finalDose > dosing.MAX_DOSE) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_DOSE;
        finalDose = dosing.MAX_DOSE;
      }
      finalDose =
        Math.floor(finalDose / dosing.ROUNDING_INCREMENT) *
        dosing.ROUNDING_INCREMENT;
      if (finalDose > dosing.MAX_DOSE) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_DOSE;
        finalDose = dosing.MAX_DOSE;
      }
    } else {
      if (finalDose > dosing.MAX_DOSE) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_DOSE;
        finalDose = dosing.MAX_DOSE;
      }
      finalDose =
        Math.floor(finalDose / dosing.ROUNDING_INCREMENT) *
        dosing.ROUNDING_INCREMENT;
      if (
        crcl >= CRCL_THRESHOLDS.LOW &&
        crcl <= CRCL_THRESHOLDS.NORMAL &&
        finalDose > dosing.MAX_INTERMEDIATE
      ) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_INTERMEDIATE;
        finalDose = dosing.MAX_INTERMEDIATE;
      } else if (crcl < CRCL_THRESHOLDS.LOW && finalDose > dosing.MAX_REDUCED) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_REDUCED;
        finalDose = dosing.MAX_REDUCED;
      } else if (crcl > CRCL_THRESHOLDS.NORMAL && finalDose > dosing.MAX_DOSE) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_DOSE;
        finalDose = dosing.MAX_DOSE;
      }
    }

    dose = finalDose;
    dosingNote = `Amikacin: ${mgPerKg} mg/kg based on lean body weight.`;
  } else if (antibiotic === "Gentamicin" || antibiotic === "Tobramycin") {
    const dosing = AMINOGLYCOSIDE_DOSING.GENTAMICIN_TOBRAMYCIN;

    // treat renalUnknown as normal renal function (use highest mg/kg/cap)
    if (renalUnknown || crcl > CRCL_THRESHOLDS.NORMAL || critical) {
      mgPerKg = dosing.NORMAL_CRITICAL;
    } else if (crcl < CRCL_THRESHOLDS.LOW) {
      mgPerKg = dosing.REDUCED;
    } else {
      mgPerKg = dosing.INTERMEDIATE;
    }

    rawDose = mgPerKg * lbwRaw; // use full precision LBW for calculation
    cidnoround = rawDose;

    // only round at the final step
    let finalDose = rawDose;
    if (critical || renalUnknown) {
      // for critical/ICU or renalUnknown, always cap at maximum dose
      if (finalDose > dosing.MAX_DOSE) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_DOSE;
        finalDose = dosing.MAX_DOSE;
      }
      finalDose =
        Math.floor(finalDose / dosing.ROUNDING_INCREMENT) *
        dosing.ROUNDING_INCREMENT;
      if (finalDose > dosing.MAX_DOSE) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_DOSE;
        finalDose = dosing.MAX_DOSE;
      }
    } else {
      if (finalDose > dosing.MAX_DOSE) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_DOSE;
        finalDose = dosing.MAX_DOSE;
      }
      finalDose =
        Math.floor(finalDose / dosing.ROUNDING_INCREMENT) *
        dosing.ROUNDING_INCREMENT;
      if (
        crcl >= CRCL_THRESHOLDS.LOW &&
        crcl <= CRCL_THRESHOLDS.NORMAL &&
        finalDose > dosing.MAX_INTERMEDIATE
      ) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_INTERMEDIATE;
        finalDose = dosing.MAX_INTERMEDIATE;
      } else if (crcl < CRCL_THRESHOLDS.LOW && finalDose > dosing.MAX_REDUCED) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_REDUCED;
        finalDose = dosing.MAX_REDUCED;
      } else if (crcl > CRCL_THRESHOLDS.NORMAL && finalDose > dosing.MAX_DOSE) {
        isCapped = true;
        maxDoseUsed = dosing.MAX_DOSE;
        finalDose = dosing.MAX_DOSE;
      }
    }

    dose = finalDose;
    dosingNote = `${antibiotic}: ${mgPerKg} mg/kg based on lean body weight.`;
  }

  return {
    dose,
    rawDose: cidnoround ? Math.round(cidnoround) : null,
    rawDoseExact: cidnoround || null, // Exact calculation for display formatting
    mgPerKg,
    lbw: lbwRaw ? lbwRaw.toFixed(2) : null,
    crcl: crcl ? crcl.toFixed(0) : null,
    ibw: ibw ? ibw.toFixed(1) : null,
    abw: abw ? abw.toFixed(2) : null,
    bmi: bmi ? bmi.toFixed(2) : null,
    weightTypeForCrCl,
    note: dosingNote,
    error: null,
    isCapped,
    maxDoseUsed,
  };
}
